import { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { AlertTriangle, Cloud, CloudRain, CloudSnow, Sun, CloudLightning, CloudDrizzle, Wind } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEFAULT_REFRESH_INTERVAL = 10; // 10 seconds default

// Digital Clock Widget Component
const ClockWidget = ({ item, scaleFactor }) => {
  const [time, setTime] = useState(new Date());
  
  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);
  
  const formatTime = () => {
    if (item.clock_format === '24h') {
      return time.toLocaleTimeString('es-ES', { 
        hour: '2-digit', 
        minute: '2-digit',
        second: item.clock_show_seconds ? '2-digit' : undefined,
        hour12: false 
      });
    }
    return time.toLocaleTimeString('es-ES', { 
      hour: '2-digit', 
      minute: '2-digit',
      second: item.clock_show_seconds ? '2-digit' : undefined,
      hour12: true 
    });
  };
  
  const formatDate = () => {
    return time.toLocaleDateString('es-ES', { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long' 
    });
  };
  
  const scaledFontSize = (item.font_size || 48) * scaleFactor;
  const dateFontSize = scaledFontSize * 0.4;
  
  return (
    <div 
      className="widget-clock flex flex-col items-center justify-center"
      style={{
        fontFamily: item.font_family || 'Inter',
        color: item.color || '#ffffff',
        textShadow: item.text_shadow ? '2px 2px 4px rgba(0,0,0,0.5)' : 'none',
      }}
    >
      <div style={{ fontSize: `${scaledFontSize}px`, fontWeight: item.font_weight || 700, lineHeight: 1 }}>
        {formatTime()}
      </div>
      {item.clock_show_date && (
        <div style={{ fontSize: `${dateFontSize}px`, opacity: 0.8, marginTop: '4px', textTransform: 'capitalize' }}>
          {formatDate()}
        </div>
      )}
    </div>
  );
};

// Weather Widget Component
const WeatherWidget = ({ item, scaleFactor }) => {
  const [weather, setWeather] = useState(null);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    const fetchWeather = async () => {
      if (!item.weather_city || !item.weather_api_key) return;
      try {
        const response = await axios.get(`${API}/proxy/weather`, {
          params: {
            city: item.weather_city,
            api_key: item.weather_api_key,
            units: item.weather_units || 'metric'
          }
        });
        setWeather(response.data);
        setError(null);
      } catch (err) {
        setError(err.response?.data?.detail || 'Error al cargar clima');
      }
    };
    fetchWeather();
    const interval = setInterval(fetchWeather, 600000); // Update every 10 minutes
    return () => clearInterval(interval);
  }, [item.weather_city, item.weather_api_key, item.weather_units]);
  
  const getWeatherIcon = (iconCode) => {
    const iconMap = {
      '01': Sun,
      '02': Cloud,
      '03': Cloud,
      '04': Cloud,
      '09': CloudDrizzle,
      '10': CloudRain,
      '11': CloudLightning,
      '13': CloudSnow,
      '50': Wind,
    };
    const code = iconCode?.substring(0, 2) || '01';
    const IconComponent = iconMap[code] || Cloud;
    return <IconComponent className="w-full h-full" />;
  };
  
  const scaledFontSize = (item.font_size || 32) * scaleFactor;
  const iconSize = scaledFontSize * 1.5;
  
  if (error) {
    return (
      <div className="flex items-center gap-2 text-red-400" style={{ fontSize: `${scaledFontSize * 0.5}px` }}>
        <AlertTriangle className="w-4 h-4" /> {error}
      </div>
    );
  }
  
  if (!weather) {
    return (
      <div className="animate-pulse" style={{ color: item.color || '#ffffff', fontSize: `${scaledFontSize * 0.5}px` }}>
        Cargando clima...
      </div>
    );
  }
  
  return (
    <div 
      className="widget-weather flex items-center gap-3"
      style={{
        fontFamily: item.font_family || 'Inter',
        color: item.color || '#ffffff',
        textShadow: item.text_shadow ? '2px 2px 4px rgba(0,0,0,0.5)' : 'none',
      }}
    >
      {item.weather_show_icon && (
        <div style={{ width: `${iconSize}px`, height: `${iconSize}px` }}>
          {getWeatherIcon(weather.icon)}
        </div>
      )}
      <div className="flex flex-col">
        <div style={{ fontSize: `${scaledFontSize}px`, fontWeight: item.font_weight || 700, lineHeight: 1 }}>
          {weather.temp}°{weather.units === 'metric' ? 'C' : 'F'}
        </div>
        <div style={{ fontSize: `${scaledFontSize * 0.4}px`, opacity: 0.8, textTransform: 'capitalize' }}>
          {weather.description}
        </div>
        <div style={{ fontSize: `${scaledFontSize * 0.35}px`, opacity: 0.6 }}>
          {weather.city}
        </div>
      </div>
    </div>
  );
};

// News Ticker Widget Component
const TickerWidget = ({ item, scaleFactor, containerWidth }) => {
  const scaledFontSize = (item.font_size || 20) * scaleFactor;
  const duration = item.ticker_speed || 30;
  
  return (
    <div 
      className="ticker-container w-full h-full flex items-center overflow-hidden"
      style={{ 
        backgroundColor: item.ticker_bg_color || '#000000',
        borderRadius: '4px',
      }}
    >
      <div 
        className="ticker-content whitespace-nowrap px-4"
        style={{ 
          color: item.ticker_text_color || '#ffffff',
          fontSize: `${scaledFontSize}px`,
          fontFamily: item.font_family || 'Inter',
          fontWeight: item.font_weight || 500,
          animationDuration: `${duration}s`,
        }}
      >
        {item.ticker_text || 'Texto del ticker...'}
      </div>
    </div>
  );
};

// RSS Feed Widget Component
const RssWidget = ({ item, scaleFactor }) => {
  const [articles, setArticles] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchRss = async () => {
      try {
        const params = { max_items: item.rss_max_items || 5 };
        if (item.rss_preset) {
          params.preset = item.rss_preset;
        } else if (item.rss_url) {
          params.url = item.rss_url;
        } else {
          return;
        }
        const response = await axios.get(`${API}/proxy/rss`, { params });
        setArticles(response.data.items || []);
        setLoading(false);
      } catch (err) {
        console.error('RSS fetch error:', err);
        setLoading(false);
      }
    };
    fetchRss();
    const interval = setInterval(fetchRss, 300000); // Refresh every 5 minutes
    return () => clearInterval(interval);
  }, [item.rss_preset, item.rss_url, item.rss_max_items]);
  
  // Rotate through articles
  useEffect(() => {
    if (articles.length === 0) return;
    const timer = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % articles.length);
    }, 8000); // Change every 8 seconds
    return () => clearInterval(timer);
  }, [articles.length]);
  
  const scaledFontSize = (item.font_size || 16) * scaleFactor;
  const article = articles[currentIndex];
  
  if (loading) {
    return (
      <div className="animate-pulse" style={{ color: item.color || '#ffffff', fontSize: `${scaledFontSize}px` }}>
        Cargando noticias...
      </div>
    );
  }
  
  if (!article) {
    return (
      <div style={{ color: item.color || '#ffffff', fontSize: `${scaledFontSize * 0.8}px`, opacity: 0.6 }}>
        No hay noticias disponibles
      </div>
    );
  }
  
  return (
    <div 
      className="h-full flex flex-col justify-center overflow-hidden"
      style={{
        fontFamily: item.font_family || 'Inter',
        color: item.color || '#ffffff',
        textShadow: item.text_shadow ? '2px 2px 4px rgba(0,0,0,0.5)' : 'none',
      }}
    >
      <div 
        className="font-semibold line-clamp-2"
        style={{ fontSize: `${scaledFontSize}px`, lineHeight: 1.3 }}
      >
        {article.title}
      </div>
      {article.description && (
        <div 
          className="opacity-70 line-clamp-2 mt-1"
          style={{ fontSize: `${scaledFontSize * 0.7}px`, lineHeight: 1.4 }}
        >
          {article.description.replace(/<[^>]*>/g, '')}
        </div>
      )}
      <div 
        className="opacity-50 mt-2"
        style={{ fontSize: `${scaledFontSize * 0.5}px` }}
      >
        {currentIndex + 1} / {articles.length}
      </div>
    </div>
  );
};

// Keep-alive component to prevent Fire TV Stick from sleeping
const KeepAlive = () => {
  const canvasRef = useRef(null);
  const frameRef = useRef(0);

  useEffect(() => {
    let animationId;
    let intervalId;
    
    // Method 1: Canvas animation (invisible activity)
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      
      const animate = () => {
        frameRef.current = (frameRef.current + 1) % 360;
        // Draw a single invisible pixel that changes
        ctx.clearRect(0, 0, 1, 1);
        ctx.fillStyle = `rgba(0,0,0,${0.001 * (frameRef.current % 10)})`;
        ctx.fillRect(0, 0, 1, 1);
        animationId = requestAnimationFrame(animate);
      };
      animate();
    }

    // Method 2: Periodic DOM micro-update
    intervalId = setInterval(() => {
      // Update a data attribute to create minimal DOM activity
      if (canvas) {
        canvas.setAttribute('data-tick', Date.now());
      }
    }, 5000); // Every 5 seconds

    // Method 3: Prevent screen from sleeping using Wake Lock API (if available)
    let wakeLock = null;
    const requestWakeLock = async () => {
      try {
        if ('wakeLock' in navigator) {
          wakeLock = await navigator.wakeLock.request('screen');
        }
      } catch (err) {
        // Wake Lock API not supported or failed, that's okay
      }
    };
    requestWakeLock();

    // Re-request wake lock when page becomes visible again
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        requestWakeLock();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Method 4: Periodic video frame request (helps with some browsers)
    const videoKeepAlive = setInterval(() => {
      // Simulate minimal activity by accessing performance.now()
      const _ = performance.now();
      // Force a minimal reflow
      if (canvas) {
        void canvas.offsetHeight;
      }
    }, 10000); // Every 10 seconds

    return () => {
      if (animationId) cancelAnimationFrame(animationId);
      if (intervalId) clearInterval(intervalId);
      if (videoKeepAlive) clearInterval(videoKeepAlive);
      if (wakeLock) wakeLock.release();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      width={1}
      height={1}
      style={{
        position: 'fixed',
        top: -10,
        left: -10,
        width: 1,
        height: 1,
        opacity: 0,
        pointerEvents: 'none',
        zIndex: -9999,
      }}
      aria-hidden="true"
    />
  );
};

// Animation configuration
const STAGGER_DELAY_MS = 50; // Base delay between items
const ENTRY_ANIMATION_DURATION = 600; // ms for entry animations
const EXIT_ANIMATION_DURATION = 400; // ms for exit animations

// Map entry animation to corresponding exit animation
const getExitAnimation = (entryAnimation) => {
  const exitMap = {
    'entry-fade': 'anim-exit-fade',
    'entry-slide-up': 'anim-exit-slide-down',
    'entry-slide-down': 'anim-exit-slide-up',
    'entry-slide-left': 'anim-exit-slide-right',
    'entry-slide-right': 'anim-exit-slide-left',
    'entry-scale': 'anim-exit-scale',
    'entry-rotate': 'anim-exit-rotate',
  };
  return exitMap[entryAnimation] || 'anim-exit-fade';
};

// Get default entry animation if none specified
const getDefaultEntryAnimation = (itemType, index) => {
  // Alternate between different entry animations based on index
  const animations = ['entry-fade', 'entry-slide-up', 'entry-scale', 'entry-slide-left', 'entry-slide-right'];
  return animations[index % animations.length];
};

const PublicMenuPage = ({ menuData: propMenuData }) => {
  const { menuId } = useParams();
  const [menu, setMenu] = useState(propMenuData || null);
  const [isLoading, setIsLoading] = useState(!propMenuData);
  const [error, setError] = useState(null);
  const [suspended, setSuspended] = useState(false);
  const [suspensionMessage, setSuspensionMessage] = useState("");
  const containerRef = useRef(null);
  const [displaySize, setDisplaySize] = useState({ width: 0, height: 0 });
  const intervalRef = useRef(null);
  
  // Animation state management
  const [animationPhase, setAnimationPhase] = useState('entering'); // 'entering' | 'visible' | 'exiting'
  const [visibleItems, setVisibleItems] = useState([]);
  const [pendingMenu, setPendingMenu] = useState(null);
  const animationTimeoutRef = useRef(null);
  const isFirstLoad = useRef(true);

  const fetchMenu = useCallback(async () => {
    // Skip fetching if data was provided via props
    if (propMenuData) {
      setMenu(propMenuData);
      setVisibleItems(propMenuData?.items || []);
      setIsLoading(false);
      return;
    }
    
    if (!menuId) return;
    
    try {
      const response = await axios.get(`${API}/public/menus/${menuId}`);
      
      // Check if suspended
      if (response.data.suspended) {
        setSuspended(true);
        setSuspensionMessage(response.data.suspension_message);
        setMenu(null);
        setVisibleItems([]);
      } else {
        setSuspended(false);
        setSuspensionMessage("");
        
        // If first load or no items, set directly
        if (isFirstLoad.current || !menu?.items?.length) {
          setMenu(response.data);
          setVisibleItems(response.data.items || []);
          setAnimationPhase('entering');
          isFirstLoad.current = false;
        } else {
          // On refresh: trigger exit animation, then update
          setPendingMenu(response.data);
          setAnimationPhase('exiting');
        }
      }
      setError(null);
    } catch (err) {
      if (!menu) {
        setError("Menú no encontrado");
      }
    } finally {
      setIsLoading(false);
    }
  }, [menuId, menu, propMenuData]);

  // Handle animation phase transitions
  useEffect(() => {
    if (animationTimeoutRef.current) {
      clearTimeout(animationTimeoutRef.current);
    }

    if (animationPhase === 'exiting' && pendingMenu) {
      // Wait for exit animations to complete, then switch to new menu
      const maxItems = visibleItems.length;
      const totalExitTime = EXIT_ANIMATION_DURATION + (maxItems * STAGGER_DELAY_MS);
      
      animationTimeoutRef.current = setTimeout(() => {
        setMenu(pendingMenu);
        setVisibleItems(pendingMenu.items || []);
        setPendingMenu(null);
        setAnimationPhase('entering');
      }, totalExitTime);
    } else if (animationPhase === 'entering') {
      // Wait for all entry animations to complete
      const maxItems = visibleItems.length;
      const totalEntryTime = ENTRY_ANIMATION_DURATION + (maxItems * STAGGER_DELAY_MS);
      
      animationTimeoutRef.current = setTimeout(() => {
        setAnimationPhase('visible');
      }, totalEntryTime);
    }

    return () => {
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }
    };
  }, [animationPhase, pendingMenu, visibleItems.length]);

  // Initial fetch
  useEffect(() => {
    fetchMenu();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Setup refresh interval based on menu config
  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    const refreshSeconds = menu?.refresh_interval || DEFAULT_REFRESH_INTERVAL;
    const refreshMs = refreshSeconds * 1000;
    
    intervalRef.current = setInterval(fetchMenu, refreshMs);
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [menu?.refresh_interval, fetchMenu]);

  // Calculate display size to fill screen while maintaining aspect ratio
  useEffect(() => {
    const updateSize = () => {
      if (!menu) return;
      
      const canvasWidth = menu.canvas_width || 1920;
      const canvasHeight = menu.canvas_height || 1080;
      const aspectRatio = canvasWidth / canvasHeight;
      
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      
      let width, height;
      
      // Fill the screen while maintaining aspect ratio
      if (viewportWidth / viewportHeight > aspectRatio) {
        // Viewport is wider - use height as constraint
        height = viewportHeight;
        width = height * aspectRatio;
      } else {
        // Viewport is taller - use width as constraint
        width = viewportWidth;
        height = width / aspectRatio;
      }
      
      setDisplaySize({ width: Math.floor(width), height: Math.floor(height) });
    };

    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, [menu]);

  if (isLoading && !menu && !suspended) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="text-stone-400 animate-pulse">Cargando menú...</div>
      </div>
    );
  }

  // Show suspension message
  if (suspended) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-stone-900 to-stone-800">
        <div className="max-w-lg mx-4 text-center">
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-2xl p-8">
            <AlertTriangle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white mb-4">Servicio Suspendido</h1>
            <p className="text-stone-300 text-lg leading-relaxed">
              {suspensionMessage}
            </p>
          </div>
          <p className="text-stone-600 text-sm mt-6">
            Si cree que esto es un error, contacte al administrador.
          </p>
        </div>
      </div>
    );
  }

  if (error && !menu) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="text-center">
          <p className="text-red-400 text-xl">{error}</p>
          <p className="text-stone-500 mt-2">El menú que buscas no existe o fue eliminado</p>
        </div>
      </div>
    );
  }

  // Get canvas config for scaling
  const canvasWidth = menu?.canvas_width || 1920;
  const canvasHeight = menu?.canvas_height || 1080;
  
  // Calculate scale factor for font sizes
  const scaleFactor = displaySize.width / canvasWidth;
  const refreshInterval = menu?.refresh_interval || DEFAULT_REFRESH_INTERVAL;

  // Helper function to get animation classes for an item
  const getItemAnimationClasses = (item, itemIndex) => {
    const classes = [];
    
    // Determine stagger index (capped at 20 for performance)
    const staggerIndex = Math.min(itemIndex, 20);
    
    if (animationPhase === 'entering') {
      // Apply entry animation
      const entryAnim = item.entry_animation && item.entry_animation !== 'none' 
        ? item.entry_animation 
        : getDefaultEntryAnimation(item.type, itemIndex);
      classes.push(`anim-${entryAnim}`);
      classes.push(`stagger-delay-${staggerIndex}`);
      classes.push('public-item-entering');
      // Also apply continuous animation class so CSS combined rules chain them
      if (item.animation && item.animation !== 'none') {
        classes.push(`anim-${item.animation}`);
      }
    } else if (animationPhase === 'exiting') {
      // Apply exit animation (reverse of entry)
      const entryAnim = item.entry_animation && item.entry_animation !== 'none' 
        ? item.entry_animation 
        : getDefaultEntryAnimation(item.type, itemIndex);
      classes.push(getExitAnimation(entryAnim));
      classes.push(`stagger-delay-${staggerIndex}`);
    } else {
      // animationPhase === 'visible' - apply continuous animations
      if (item.animation && item.animation !== 'none') {
        classes.push(`anim-${item.animation}`);
      }
    }
    
    return classes.join(' ');
  };

  // Calculate animation delay for an item
  const getItemAnimationDelay = (item, itemIndex) => {
    // Use item's custom delay if specified, otherwise use stagger delay
    if (item.animation_delay && animationPhase === 'visible') {
      return `${item.animation_delay}s`;
    }
    
    const staggerIndex = Math.min(itemIndex, 20);
    return `${staggerIndex * STAGGER_DELAY_MS}ms`;
  };

  return (
    <div 
      className="min-h-screen w-full bg-black flex items-center justify-center overflow-hidden"
      data-testid="public-menu-page"
    >
      {/* Keep-alive component to prevent Fire TV Stick from sleeping */}
      <KeepAlive />
      
      {/* Canvas container - exact same positioning as editor */}
      <div
        ref={containerRef}
        className={`relative public-menu-container ${animationPhase === 'exiting' ? '' : ''}`}
        style={{
          width: displaySize.width,
          height: displaySize.height,
          backgroundImage: menu?.background_image ? `url(${menu.background_image})` : 'none',
          backgroundColor: '#1c1917',
          backgroundSize: '100% 100%',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        {/* Render menu items using exact same percentage positioning and scaled fonts */}
        {visibleItems?.sort((a, b) => (a.z_index || 0) - (b.z_index || 0)).map((item, itemIndex) => {
          // Calculate pixel position from percentage (same as editor)
          const pixelX = (item.x / 100) * displaySize.width;
          const pixelY = (item.y / 100) * displaySize.height;
          
          // Scale font size proportionally to display size
          const scaledFontSize = item.font_size * scaleFactor;
          
          // For images: scale width and height
          const scaledWidth = item.type === 'image' ? (item.width / 100) * displaySize.width : 'auto';
          const scaledHeight = item.type === 'image' ? (item.height / 100) * displaySize.height : 'auto';
          
          // Text shadow style
          const textShadowStyle = item.text_shadow 
            ? '2px 2px 4px rgba(0,0,0,0.8), 1px 1px 2px rgba(0,0,0,0.6)' 
            : 'none';

          // For videos/gifs: scale width and height
          const scaledVideoWidth = (item.type === 'video' || item.type === 'gif') ? (item.width / 100) * displaySize.width : 'auto';
          const scaledVideoHeight = (item.type === 'video' || item.type === 'gif') ? (item.height / 100) * displaySize.height : 'auto';
          
          // Get animation classes based on current phase
          const animationClasses = getItemAnimationClasses(item, itemIndex);
          const animationDelay = getItemAnimationDelay(item, itemIndex);
          
          if (item.type === 'image') {
            return (
              <img
                key={item.id}
                src={item.image_url}
                alt={item.label || 'Image'}
                className={`absolute ${animationClasses}`}
                style={{
                  left: `${pixelX}px`,
                  top: `${pixelY}px`,
                  width: scaledWidth,
                  height: scaledHeight,
                  objectFit: 'contain',
                  zIndex: item.z_index || 0,
                  opacity: animationPhase === 'entering' || animationPhase === 'visible' ? (item.opacity ?? 1) : undefined,
                  animationDelay: animationDelay,
                }}
                data-testid={`public-item-${item.id}`}
              />
            );
          }

          // Video rendering
          if (item.type === 'video' || item.type === 'gif') {
            // GIF - render as image
            if (item.video_type === 'gif') {
              return (
                <img
                  key={item.id}
                  src={item.video_url}
                  alt={item.label || 'GIF'}
                  className={`absolute ${animationClasses}`}
                  style={{
                    left: `${pixelX}px`,
                    top: `${pixelY}px`,
                    width: scaledVideoWidth,
                    height: scaledVideoHeight,
                    objectFit: 'contain',
                    zIndex: item.z_index || 0,
                    opacity: animationPhase === 'entering' || animationPhase === 'visible' ? (item.opacity ?? 1) : undefined,
                    animationDelay: animationDelay,
                  }}
                  data-testid={`public-item-${item.id}`}
                />
              );
            }

            // YouTube - render as iframe (animations applied to wrapper div)
            if (item.video_type === 'youtube') {
              return (
                <div
                  key={item.id}
                  className={`absolute ${animationClasses}`}
                  style={{
                    left: `${pixelX}px`,
                    top: `${pixelY}px`,
                    width: scaledVideoWidth,
                    height: scaledVideoHeight,
                    zIndex: item.z_index || 0,
                    opacity: animationPhase === 'entering' || animationPhase === 'visible' ? (item.opacity ?? 1) : undefined,
                    animationDelay: animationDelay,
                  }}
                  data-testid={`public-item-${item.id}`}
                >
                  <iframe
                    src={item.video_url}
                    title={item.label || 'YouTube Video'}
                    style={{
                      width: '100%',
                      height: '100%',
                      border: 'none',
                    }}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              );
            }

            // Vimeo - render as iframe (animations applied to wrapper div)
            if (item.video_type === 'vimeo') {
              return (
                <div
                  key={item.id}
                  className={`absolute ${animationClasses}`}
                  style={{
                    left: `${pixelX}px`,
                    top: `${pixelY}px`,
                    width: scaledVideoWidth,
                    height: scaledVideoHeight,
                    zIndex: item.z_index || 0,
                    opacity: animationPhase === 'entering' || animationPhase === 'visible' ? (item.opacity ?? 1) : undefined,
                    animationDelay: animationDelay,
                  }}
                  data-testid={`public-item-${item.id}`}
                >
                  <iframe
                    src={item.video_url}
                    title={item.label || 'Vimeo Video'}
                    style={{
                      width: '100%',
                      height: '100%',
                      border: 'none',
                    }}
                    allow="autoplay; fullscreen; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              );
            }

            // Direct URL video - render as video element (no storage)
            if (item.video_type === 'direct_url') {
              return (
                <video
                  key={item.id}
                  src={item.video_url}
                  className={`absolute ${animationClasses}`}
                  style={{
                    left: `${pixelX}px`,
                    top: `${pixelY}px`,
                    width: scaledVideoWidth,
                    height: scaledVideoHeight,
                    objectFit: 'contain',
                    zIndex: item.z_index || 0,
                    opacity: animationPhase === 'entering' || animationPhase === 'visible' ? (item.opacity ?? 1) : undefined,
                    animationDelay: animationDelay,
                  }}
                  muted={item.video_muted !== false}
                  loop={item.video_loop !== false}
                  autoPlay={item.video_autoplay !== false}
                  playsInline
                  crossOrigin="anonymous"
                  data-testid={`public-item-${item.id}`}
                />
              );
            }

            // Uploaded video - render as video element
            return (
              <video
                key={item.id}
                src={item.video_url}
                className={`absolute ${animationClasses}`}
                style={{
                  left: `${pixelX}px`,
                  top: `${pixelY}px`,
                  width: scaledVideoWidth,
                  height: scaledVideoHeight,
                  objectFit: 'contain',
                  zIndex: item.z_index || 0,
                  opacity: animationPhase === 'entering' || animationPhase === 'visible' ? (item.opacity ?? 1) : undefined,
                  animationDelay: animationDelay,
                }}
                muted={item.video_muted !== false}
                loop={item.video_loop !== false}
                autoPlay={item.video_autoplay !== false}
                playsInline
                data-testid={`public-item-${item.id}`}
              />
            );
          }

          // Shape rendering
          if (item.type === 'shape') {
            const shapeWidth = (item.width / 100) * displaySize.width;
            const shapeHeight = (item.height / 100) * displaySize.height;
            
            const shapeTransform = [
              item.shape_rotation ? `rotate(${item.shape_rotation}deg)` : '',
              item.shape_skew_x ? `skewX(${item.shape_skew_x}deg)` : '',
              item.shape_skew_y ? `skewY(${item.shape_skew_y}deg)` : '',
            ].filter(Boolean).join(' ');

            const baseShapeStyle = {
              left: `${pixelX}px`,
              top: `${pixelY}px`,
              zIndex: item.z_index || 0,
              opacity: animationPhase === 'entering' || animationPhase === 'visible' ? (item.opacity ?? 1) : undefined,
              animationDelay: animationDelay,
            };

            if (item.shape_type === 'triangle') {
              return (
                <div
                  key={item.id}
                  className={`absolute ${animationClasses}`}
                  style={{
                    ...baseShapeStyle,
                    width: 0,
                    height: 0,
                    borderLeft: `${shapeWidth / 2}px solid transparent`,
                    borderRight: `${shapeWidth / 2}px solid transparent`,
                    borderBottom: `${shapeHeight}px solid ${item.shape_fill || '#3b82f6'}`,
                    transform: shapeTransform || undefined,
                  }}
                  data-testid={`public-item-${item.id}`}
                />
              );
            }

            return (
              <div
                key={item.id}
                className={`absolute ${animationClasses}`}
                style={{
                  ...baseShapeStyle,
                  width: shapeWidth,
                  height: shapeHeight,
                  backgroundColor: item.shape_fill || '#3b82f6',
                  border: item.shape_stroke_width > 0 ? `${item.shape_stroke_width}px solid ${item.shape_stroke || '#1e40af'}` : 'none',
                  borderRadius: item.shape_type === 'circle' ? '50%' : `${item.shape_border_radius || 0}px`,
                  transform: shapeTransform || undefined,
                }}
                data-testid={`public-item-${item.id}`}
              />
            );
          }
          
          // Widget rendering - Clock
          if (item.type === 'clock') {
            const widgetWidth = (item.width / 100) * displaySize.width;
            const widgetHeight = (item.height / 100) * displaySize.height;
            
            return (
              <div
                key={item.id}
                className={`absolute ${animationClasses}`}
                style={{
                  left: `${pixelX}px`,
                  top: `${pixelY}px`,
                  width: widgetWidth,
                  height: widgetHeight,
                  zIndex: item.z_index || 0,
                  opacity: animationPhase === 'entering' || animationPhase === 'visible' ? (item.opacity ?? 1) : undefined,
                  animationDelay: animationDelay,
                }}
                data-testid={`public-item-${item.id}`}
              >
                <ClockWidget item={item} scaleFactor={scaleFactor} />
              </div>
            );
          }
          
          // Widget rendering - Weather
          if (item.type === 'weather') {
            const widgetWidth = (item.width / 100) * displaySize.width;
            const widgetHeight = (item.height / 100) * displaySize.height;
            
            return (
              <div
                key={item.id}
                className={`absolute ${animationClasses}`}
                style={{
                  left: `${pixelX}px`,
                  top: `${pixelY}px`,
                  width: widgetWidth,
                  height: widgetHeight,
                  zIndex: item.z_index || 0,
                  opacity: animationPhase === 'entering' || animationPhase === 'visible' ? (item.opacity ?? 1) : undefined,
                  animationDelay: animationDelay,
                }}
                data-testid={`public-item-${item.id}`}
              >
                <WeatherWidget item={item} scaleFactor={scaleFactor} />
              </div>
            );
          }
          
          // Widget rendering - Ticker
          if (item.type === 'ticker') {
            const widgetWidth = (item.width / 100) * displaySize.width;
            const widgetHeight = (item.height / 100) * displaySize.height;
            
            return (
              <div
                key={item.id}
                className={`absolute ${animationClasses}`}
                style={{
                  left: `${pixelX}px`,
                  top: `${pixelY}px`,
                  width: widgetWidth,
                  height: widgetHeight,
                  zIndex: item.z_index || 0,
                  opacity: animationPhase === 'entering' || animationPhase === 'visible' ? (item.opacity ?? 1) : undefined,
                  animationDelay: animationDelay,
                }}
                data-testid={`public-item-${item.id}`}
              >
                <TickerWidget item={item} scaleFactor={scaleFactor} containerWidth={widgetWidth} />
              </div>
            );
          }
          
          // Widget rendering - RSS
          if (item.type === 'rss') {
            const widgetWidth = (item.width / 100) * displaySize.width;
            const widgetHeight = (item.height / 100) * displaySize.height;
            
            return (
              <div
                key={item.id}
                className={`absolute ${animationClasses}`}
                style={{
                  left: `${pixelX}px`,
                  top: `${pixelY}px`,
                  width: widgetWidth,
                  height: widgetHeight,
                  zIndex: item.z_index || 0,
                  opacity: animationPhase === 'entering' || animationPhase === 'visible' ? (item.opacity ?? 1) : undefined,
                  animationDelay: animationDelay,
                }}
                data-testid={`public-item-${item.id}`}
              >
                <RssWidget item={item} scaleFactor={scaleFactor} />
              </div>
            );
          }
          
          // Default: Text rendering
          return (
            <div
              key={item.id}
              className={`absolute menu-item-text ${animationClasses}`}
              style={{
                left: `${pixelX}px`,
                top: `${pixelY}px`,
                fontFamily: item.font_family,
                fontSize: `${scaledFontSize}px`,
                fontWeight: item.font_weight,
                color: item.color,
                lineHeight: 1.2,
                whiteSpace: 'nowrap',
                textShadow: textShadowStyle,
                zIndex: item.z_index || 0,
                opacity: animationPhase === 'entering' || animationPhase === 'visible' ? (item.opacity ?? 1) : undefined,
                animationDelay: animationDelay,
              }}
              data-testid={`public-item-${item.id}`}
            >
              {item.text}
            </div>
          );
        })}
      </div>

      {/* Subtle info indicator */}
      <div className="fixed bottom-2 right-2 text-stone-700 text-[10px] opacity-30 hover:opacity-100 transition-opacity">
        Refresh: {refreshInterval}s
      </div>
    </div>
  );
};

export default PublicMenuPage;
